import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AuthUser } from '@/services/authService';
import { UserRole } from '@/types/enums';

interface UseBranchIdResult {
  branchId: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Resolve cabang untuk pengguna saat ini.
 *
 * PENGELOLA          → cabang yang ditugaskan via pengelola_profiles
 * ADMIN_BANK_SAMPAH  → cabang pertama aktif di bank_sampah miliknya
 *
 * Hasilnya stabil selama user tidak berganti; tidak perlu refetch manual.
 */
export function useBranchId(user: AuthUser | null): UseBranchIdResult {
  const [branchId, setBranchId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async (): Promise<void> => {
      // Tidak ada user — reset loading, tidak ada yang perlu diresolve
      if (!user) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // 1. Coba pengelola_profiles (berlaku untuk PENGELOLA dan admin
      //    yang juga punya penugasan cabang).
      const { data: profile } = await supabase
        .from('pengelola_profiles')
        .select('branch_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profile?.branch_id) {
        if (!cancelled) { setBranchId(profile.branch_id); setLoading(false); }
        return;
      }

      // 2. ADMIN_BANK_SAMPAH → bank_sampah_admins → branches
      if (user.role === UserRole.ADMIN_BANK_SAMPAH) {
        const { data: adminEntry } = await supabase
          .from('bank_sampah_admins')
          .select('bank_sampah_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!adminEntry?.bank_sampah_id) {
          if (!cancelled) {
            setError('Akun admin belum ditugaskan ke bank sampah. Hubungi Super Admin.');
            setLoading(false);
          }
          return;
        }

        const { data: branch, error: branchErr } = await supabase
          .from('branches')
          .select('id')
          .eq('bank_sampah_id', adminEntry.bank_sampah_id)
          .eq('is_active', true)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (!cancelled) {
          if (branch?.id) {
            setBranchId(branch.id);
          } else {
            setError(branchErr ? 'Gagal memuat cabang.' : 'Belum ada cabang aktif.');
          }
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setError('Cabang tidak ditemukan. Pastikan akun sudah dikonfigurasi.');
        setLoading(false);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  return { branchId, loading, error };
}
